import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockGadgetSubmissions } from '../../data/mockData';
import { ShieldCheck, CheckCircle, XCircle, FileText } from 'lucide-react';
import { GadgetSubmission } from '../../types';
export const DsApprovalPage: React.FC = () => {
  const navigate = useNavigate();
  const pendingSubmissions = mockGadgetSubmissions.filter(g => g.status === 'submitted');
  const [selectedSub, setSelectedSub] = useState<GadgetSubmission | null>(pendingSubmissions.length > 0 ? pendingSubmissions[0] : null);
  const [smarakNo, setSmarakNo] = useState('');
  const [approvalDate, setApprovalDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  if (!selectedSub) {
    return <div className="max-w-6xl mx-auto text-center py-20" data-id="element-469">
        <ShieldCheck size={64} className="mx-auto text-slate-300 mb-4" data-id="element-470" />
        <h2 className="text-xl font-semibold text-slate-700" data-id="element-471">
          কোন অপেক্ষমাণ অনুমোদন নেই
        </h2>
        <p className="text-slate-500" data-id="element-472">
          সকল গেজেট জমাদান পর্যালোচনা করা হয়েছে।
        </p>
      </div>;
  }
  return <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6" data-id="element-473">
      {/* Sidebar List */}
      <div className="w-full lg:w-1/3 flex flex-col gap-4" data-id="element-474">
        <div className="mb-2" data-id="element-475">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-476">গেজেট অনুমোদন</h1>
          <p className="text-sm text-slate-500" data-id="element-477">
            পর্যালোচনা এবং প্রজ্ঞাপনের জন্য অনুমোদন করুন
          </p>
        </div>

        <div className="card overflow-hidden flex-1" data-id="element-478">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200" data-id="element-479">
            <h2 className="font-semibold text-slate-800 text-sm" data-id="element-480">
              অনুমোদনের অপেক্ষায় ({pendingSubmissions.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto" data-id="element-481">
            {pendingSubmissions.map(sub => <div key={sub.id} onClick={() => {
            setSelectedSub(sub);
            setSmarakNo('');
            setRemarks('');
          }} className={`p-4 cursor-pointer transition-colors ${selectedSub.id === sub.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : 'hover:bg-slate-50 border-l-4 border-transparent'}`} data-id="element-482">
                <div className="flex justify-between items-start mb-1" data-id="element-483">
                  <span className="font-semibold text-slate-900 text-sm" data-id="element-484">
                    {sub.district}, {sub.upazila}
                  </span>
                  <span className="text-xs font-medium text-slate-500" data-id="element-485">
                    {sub.id}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mb-2" data-id="element-486">
                  জমাদানকারী: {sub.submittedBy}
                </p>
                <div className="flex justify-between items-center" data-id="element-487">
                  <span className="text-xs font-medium bg-slate-100 px-2 py-1 rounded text-slate-700" data-id="element-488">
                    {sub.moujaDetails.length} মৌজা
                  </span>
                  <span className="text-xs text-slate-500" data-id="element-489">
                    {new Date(sub.submittedAt).toLocaleDateString('bn-BD')}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Detail View */}
      <div className="w-full lg:w-2/3 flex flex-col gap-4" data-id="element-490">
        <div className="card p-6 flex-1 flex flex-col" data-id="element-491">
          <div className="flex justify-between items-start mb-6 border-b border-slate-200 pb-4" data-id="element-492">
            <div data-id="element-493">
              <h2 className="text-xl font-bold text-slate-900 mb-1" data-id="element-494">
                গেজেট আইডি: {selectedSub.id}
              </h2>
              <p className="text-sm text-slate-500" data-id="element-495">
                {selectedSub.district}, {selectedSub.upazila} • দাখিল করেছেন{' '}
                {selectedSub.submittedBy}
              </p>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-496">
              জমাদান করা হয়েছে
            </span>
          </div>

          <div className="flex-1 overflow-y-auto mb-6" data-id="element-497">
            <h3 className="text-sm font-semibold text-slate-800 mb-3" data-id="element-498">
              মৌজা ও খতিয়ান তথ্য
            </h3>
            <div className="overflow-x-auto border border-slate-200 rounded-md" data-id="element-499">
              <table className="min-w-full divide-y divide-slate-200" data-id="element-500">
                <thead className="bg-slate-50" data-id="element-501">
                  <tr data-id="element-502">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-503">
                      মৌজার নাম
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-504">
                      জে.এল. নম্বর
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-505">
                      শুরু নং
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-506">
                      শেষ নং
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-507">
                      খতিয়ান সংখ্যা
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-508">
                      শীট সংখ্যা
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200" data-id="element-509">
                  {selectedSub.moujaDetails.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-510">
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-511">
                        {row.mouja}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-512">
                        {row.jlNumber}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-513">
                        {row.khatianStartNo || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-514">
                        {row.khatianEndNo || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-515">
                        {row.totalKhatian}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700" data-id="element-516">
                        {row.totalSheet}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-slate-50 p-6 rounded-lg border border-slate-200" data-id="element-517">
            <h3 className="text-sm font-semibold text-slate-800 mb-4 uppercase tracking-wider" data-id="element-518">
              অনুমোদন কার্যক্রম
            </h3>

            <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-519">
              <div data-id="element-520">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-521">
                  স্মারক নং (অনুমোদনের জন্য আবশ্যক)
                </label>
                <input type="text" className="input-field text-sm" placeholder="যেমন: ৩১.০০.০০০০.০৮৯.৩৩.০০১.২৫.৩৭" value={smarakNo} onChange={e => setSmarakNo(e.target.value)} data-id="element-522" />
              </div>
              <div data-id="element-523">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-524">
                  তারিখ
                </label>
                <input type="date" className="input-field text-sm" value={approvalDate} onChange={e => setApprovalDate(e.target.value)} data-id="element-525" />
              </div>
            </div>

            <div className="mb-6" data-id="element-526">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-527">
                মন্তব্য (ফেরত পাঠানোর জন্য আবশ্যক)
              </label>
              <textarea className="input-field text-sm" rows={3} placeholder="মন্তব্য বা সংশোধনের কারণ লিখুন..." value={remarks} onChange={e => setRemarks(e.target.value)} data-id="element-528"></textarea>
            </div>

            <div className="flex space-x-4" data-id="element-529">
              <button className="btn-danger flex-1 py-3 flex justify-center items-center font-semibold text-sm shadow-sm" disabled={!remarks} onClick={() => navigate('/gadget/ds-submissions')} data-id="element-530">
                <XCircle size={18} className="mr-2" data-id="element-531" /> সংশোধনের জন্য ফেরত দিন
              </button>
              <button className="btn-success flex-1 py-3 flex justify-center items-center font-semibold text-sm shadow-sm" disabled={!smarakNo} onClick={() => navigate('/gadget/projnapan')} data-id="element-532">
                <CheckCircle size={18} className="mr-2" data-id="element-533" /> অনুমোদন করুন
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};